import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, AlertTriangle, XCircle, Plus, Minus, ChevronDown, ChevronRight, Infinity } from 'lucide-react';
import { inventoryApi } from '../../api/endpoints/inventory';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { StatCard } from '../../components/ui/StatCard';
import { SearchInput } from '../../components/ui/SearchInput';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { EmptyState } from '../../components/ui/EmptyState';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Pagination } from '../../components/ui/Pagination';
import { useToast } from '../../components/ui/Alert';
import { usePagination } from '../../hooks/usePagination';
import { useDebounce } from '../../hooks/useDebounce';
import { getCdnUrl, formatCurrency } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';
import type { InventoryItem, InventoryVariant } from '../../types/inventory';

function getVariantLabel(combination: Record<string, string>): string {
  return Object.values(combination).join(' / ') || '-';
}

const UNLIMITED_STOCK = -1;

function isUnlimitedStock(quantity: number): boolean {
  return quantity === UNLIMITED_STOCK;
}

function StockStatusBadge({ quantity, lowStockThreshold, t }: { quantity: number; lowStockThreshold?: number; t: (key: string) => string }) {
  if (isUnlimitedStock(quantity)) return <Badge variant="success">{t('inventory.inStock')}</Badge>;
  if (quantity <= 0) return <Badge variant="danger">{t('inventory.outOfStock')}</Badge>;
  if (quantity <= (lowStockThreshold ?? 5)) return <Badge variant="warning">{t('inventory.lowStock')}</Badge>;
  return <Badge variant="success">{t('inventory.inStock')}</Badge>;
}

export function InventoryPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentStore } = useStore();
  const [filter, setFilter] = useState<'all' | 'low' | 'out'>('all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<number>(0);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  // Variant editing state: keyed by `${productId}-${variantId}`
  const [editingVariantId, setEditingVariantId] = useState<string | null>(null);
  const [editVariantValue, setEditVariantValue] = useState<number>(0);

  // Reset to page 1 when the search or filter changes (results would otherwise
  // be hidden behind a now-out-of-range page).
  const { page, limit, goToPage } = usePagination({
    resetDeps: [debouncedSearch, filter],
  });

  const { data, isLoading } = useQuery({
    queryKey: ['inventory', storeId, { page, limit, filter, search: debouncedSearch }],
    queryFn: () => inventoryApi.list(storeId!, { page, limit, filter, search: debouncedSearch || undefined }),
    enabled: !!storeId,
  });

  const updateMutation = useMutation({
    mutationFn: ({ productId, quantity }: { productId: string; quantity: number }) =>
      inventoryApi.updateStock(storeId!, productId, quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', storeId] });
      toast('success', t('common.success'));
      setEditingId(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const updateVariantMutation = useMutation({
    mutationFn: ({ productId, variantId, quantity }: { productId: string; variantId: string; quantity: number }) =>
      inventoryApi.updateVariantStock(storeId!, productId, variantId, quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', storeId] });
      toast('success', t('common.success'));
      setEditingVariantId(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const toggleExpanded = (productId: string) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const stats = data?.stats;
  const items: InventoryItem[] = data?.items ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.pages ?? 1;

  const hasVariants = (item: InventoryItem) => item.variants && item.variants.length > 0;

  const renderStockControls = (
    quantity: number,
    isEditing: boolean,
    currentEditValue: number,
    onEditValueChange: (v: number) => void,
    onSave: () => void,
    onCancel: () => void,
    onStartEdit: () => void,
    onIncrement: () => void,
    onDecrement: () => void,
    onToggleUnlimited: () => void,
    isOutOfStock: boolean,
    fallbackContent: React.ReactNode,
  ) => {
    const unlimited = isUnlimitedStock(quantity);
    return (
      <PermissionGate requires="products.edit" fallback={fallbackContent}>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={currentEditValue}
              onChange={(e) => onEditValueChange(Number(e.target.value))}
              className="w-20 rounded border border-accent bg-bg-field px-2 py-1 text-sm outline-none"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSave();
                if (e.key === 'Escape') onCancel();
              }}
            />
            <Button size="sm" onClick={onSave}>
              {t('common.save')}
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <button
              onClick={onDecrement}
              disabled={unlimited || quantity <= 0}
              className="flex h-6 w-6 items-center justify-center rounded border border-border-default text-text-muted hover:bg-bg-hover hover:text-text-primary transition-colors disabled:opacity-30"
            >
              <Minus className="h-3 w-3" />
            </button>
            <button
              onClick={unlimited ? undefined : onStartEdit}
              className={`min-w-[2rem] text-center text-sm cursor-pointer ${unlimited ? 'text-accent font-semibold' : isOutOfStock ? 'text-danger font-medium hover:text-accent' : 'hover:text-accent'}`}
            >
              {unlimited ? '∞' : quantity}
            </button>
            <button
              onClick={onIncrement}
              disabled={unlimited}
              className="flex h-6 w-6 items-center justify-center rounded border border-border-default text-text-muted hover:bg-bg-hover hover:text-text-primary transition-colors disabled:opacity-30"
            >
              <Plus className="h-3 w-3" />
            </button>
            <button
              onClick={onToggleUnlimited}
              title={t('inventory.setUnlimited', 'Set unlimited stock')}
              className={`flex h-6 w-6 items-center justify-center rounded border transition-colors ${unlimited ? 'border-accent bg-accent/15 text-accent hover:bg-accent/25' : 'border-border-default text-text-muted hover:bg-bg-hover hover:text-text-primary'}`}
            >
              <Infinity className="h-3 w-3" />
            </button>
          </div>
        )}
      </PermissionGate>
    );
  };

  const renderProductRow = (item: InventoryItem) => {
    const expanded = expandedProducts.has(item._id);
    const itemHasVariants = hasVariants(item);

    return (
      <tr
        key={item._id}
        className="border-b border-border-default/40 transition-colors hover:bg-bg-hover/60"
      >
        {/* Expand toggle */}
        <td className="w-10 px-2 py-3.5">
          {itemHasVariants ? (
            <button
              onClick={() => toggleExpanded(item._id)}
              className="flex h-6 w-6 items-center justify-center rounded text-text-muted hover:bg-bg-hover hover:text-text-primary transition-colors"
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <div className="h-6 w-6" />
          )}
        </td>
        {/* Image */}
        <td className="w-16 px-4 py-3.5 text-sm">
          {item.image ? (
            <img src={getCdnUrl(item.image, currentStore?._id)} alt="" className="h-10 w-10 rounded-lg object-cover" />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-bg-tertiary" />
          )}
        </td>
        {/* Name */}
        <td className="px-4 py-3.5 text-sm">
          <div>
            <p className="font-medium">{item.title}</p>
            {item.sku && <p className="text-xs text-text-muted">{item.sku}</p>}
            {itemHasVariants && (
              <p className="text-xs text-text-muted mt-0.5">
                {item.variants!.length} {t('inventory.variants').toLowerCase()}
              </p>
            )}
          </div>
        </td>
        {/* Price */}
        <td className="px-4 py-3.5 text-sm">
          <span className="text-sm">{formatCurrency(item.price, currentStore?.currency)}</span>
        </td>
        {/* Quantity */}
        <td className="px-4 py-3.5 text-sm">
          {renderStockControls(
            item.quantity,
            editingId === item._id,
            editValue,
            setEditValue,
            () => updateMutation.mutate({ productId: item._id, quantity: editValue }),
            () => setEditingId(null),
            () => { setEditingId(item._id); setEditValue(item.quantity); },
            () => updateMutation.mutate({ productId: item._id, quantity: item.quantity + 1 }),
            () => updateMutation.mutate({ productId: item._id, quantity: Math.max(0, item.quantity - 1) }),
            () => updateMutation.mutate({ productId: item._id, quantity: isUnlimitedStock(item.quantity) ? 0 : UNLIMITED_STOCK }),
            item.quantity <= 0 && !isUnlimitedStock(item.quantity),
            <span className={isUnlimitedStock(item.quantity) ? 'text-accent font-semibold' : item.quantity <= 0 ? 'text-danger font-medium' : ''}>{isUnlimitedStock(item.quantity) ? '∞' : item.quantity}</span>,
          )}
        </td>
        {/* Status */}
        <td className="px-4 py-3.5 text-sm">
          <StockStatusBadge quantity={item.quantity} lowStockThreshold={item.lowStockThreshold} t={t} />
        </td>
      </tr>
    );
  };

  const renderVariantRows = (item: InventoryItem) => {
    if (!expandedProducts.has(item._id) || !item.variants || item.variants.length === 0) {
      return null;
    }

    return item.variants.map((variant: InventoryVariant) => {
      const compositeKey = `${item._id}-${variant._id}`;
      const isEditing = editingVariantId === compositeKey;

      return (
        <tr
          key={compositeKey}
          className="border-b border-border-default/40 bg-bg-tertiary/30"
        >
          {/* Expand toggle (empty) */}
          <td className="w-10 px-2 py-3" />
          {/* Image (empty, indented) */}
          <td className="w-16 px-4 py-3" />
          {/* Name - indented */}
          <td className="px-4 py-3 text-sm">
            <div className="pl-6">
              <p className="text-text-secondary">{getVariantLabel(variant.combination)}</p>
              {variant.sku && <p className="text-xs text-text-muted">{variant.sku}</p>}
            </div>
          </td>
          {/* Price (empty for variants) */}
          <td className="px-4 py-3 text-sm" />
          {/* Quantity */}
          <td className="px-4 py-3 text-sm">
            {renderStockControls(
              variant.quantity,
              isEditing,
              editVariantValue,
              setEditVariantValue,
              () => updateVariantMutation.mutate({ productId: item._id, variantId: variant._id, quantity: editVariantValue }),
              () => setEditingVariantId(null),
              () => { setEditingVariantId(compositeKey); setEditVariantValue(variant.quantity); },
              () => updateVariantMutation.mutate({ productId: item._id, variantId: variant._id, quantity: variant.quantity + 1 }),
              () => updateVariantMutation.mutate({ productId: item._id, variantId: variant._id, quantity: Math.max(0, variant.quantity - 1) }),
              () => updateVariantMutation.mutate({ productId: item._id, variantId: variant._id, quantity: isUnlimitedStock(variant.quantity) ? 0 : UNLIMITED_STOCK }),
              variant.quantity <= 0 && !isUnlimitedStock(variant.quantity),
              <span className={isUnlimitedStock(variant.quantity) ? 'text-accent font-semibold' : variant.quantity <= 0 ? 'text-danger font-medium' : ''}>{isUnlimitedStock(variant.quantity) ? '∞' : variant.quantity}</span>,
            )}
          </td>
          {/* Status */}
          <td className="px-4 py-3 text-sm">
            <StockStatusBadge quantity={variant.quantity} lowStockThreshold={item.lowStockThreshold} t={t} />
          </td>
        </tr>
      );
    });
  };

  return (
    <div>
      <h1 className="text-xl font-bold mb-6">{t('sidebar.inventory')}</h1>

      {stats && (
        <div className="grid gap-4 sm:grid-cols-4 mb-6">
          <StatCard title={t('inventory.totalProducts')} value={stats.totalProducts} icon={<Package className="h-5 w-5" />} />
          <StatCard title={t('inventory.lowStock')} value={stats.lowStock} icon={<AlertTriangle className="h-5 w-5" />} />
          <StatCard title={t('inventory.outOfStock')} value={stats.outOfStock} icon={<XCircle className="h-5 w-5" />} />
          <StatCard title={t('inventory.totalValue')} value={stats.totalValue} currency={currentStore?.currency} />
        </div>
      )}

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('inventory.searchPlaceholder', 'Search by name or SKU...')}
          className="w-full sm:w-64"
        />
        <div className="flex gap-2">
          {(['all', 'low', 'out'] as const).map((f) => (
            <Button key={f} variant={filter === f ? 'primary' : 'secondary'} size="sm" onClick={() => setFilter(f)}>
              {t(`inventory.filter_${f}`)}
            </Button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border-default bg-bg-secondary">
        {isLoading ? (
          <LoadingSpinner fullPage />
        ) : items.length === 0 ? (
          <EmptyState title={t('common.noResults')} />
        ) : (
          <div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-default bg-bg-tertiary/50">
                    <th className="w-10 px-2 py-3" />
                    <th className="w-16 px-4 py-3 text-start text-xs font-semibold text-text-secondary">
                      {t('common.image')}
                    </th>
                    <th className="px-4 py-3 text-start text-xs font-semibold text-text-secondary">
                      {t('common.name')}
                    </th>
                    <th className="px-4 py-3 text-start text-xs font-semibold text-text-secondary">
                      {t('common.price')}
                    </th>
                    <th className="px-4 py-3 text-start text-xs font-semibold text-text-secondary">
                      {t('common.quantity')}
                    </th>
                    <th className="px-4 py-3 text-start text-xs font-semibold text-text-secondary">
                      {t('common.status')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <Fragment key={item._id}>
                      {renderProductRow(item)}
                      {renderVariantRows(item)}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {page && totalPages && total && limit && (
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={goToPage}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
